import { Clock, Stethoscope } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { usePatientTATTimer } from "@/hooks/usePatientTATTimer";
import { useMDCompletionTime } from "@/hooks/useMDCompletionTime";

export interface StaffInfo {
  id: string;
  fullName: string;
  email?: string;
  phone?: string;
  role?: string;
  department?: string;
  designation?: string;
}

export interface ActivityHistoryEntry {
  id: string;
  actionType: 'assigned' | 'started' | 'paused' | 'resumed' | 'completed' | 'reassigned';
  timestamp: string;
  durationMinutes?: number;
  notes?: string;
  staff: StaffInfo;
}

export interface ActivityStep {
  id: string;
  name: string;
  status?: "completed" | "in-progress" | "pending" | "skipped";
  timestamp?: string;
  elapsedTime?: string;
  description?: string;
  
  // Enhanced staff tracking
  completedBy?: StaffInfo;
  assignedTo?: StaffInfo;
  assignedAt?: string;
  startedAt?: string;
  completionTime?: string;
  timeTakenMinutes?: number;
  notes?: string;
  
  // TAT tracking fields
  workflowStep?: string;
  previousStep?: string;
  patientId?: string;
  
  // Legacy fields (backward compatibility)
  doctor?: string;
  staffName?: string;
  
  // Activity history
  history?: ActivityHistoryEntry[];
  
  // Parallel workflow fields
  isParallelGroup?: boolean;
  parallelActivities?: ActivityStep[];
  isParallelWorkflow?: boolean;
  parallelPaths?: {
    id: string;
    name: string;
    steps: ActivityStep[];
  }[];
  completedCount?: number;
  totalCount?: number;
  isMergePoint?: boolean;
}


export interface Patient {
  name: string;
  id: string;
  uuid: string; // Database UUID for querying activities (REQUIRED)
  age: number;
  gender: "M" | "F" | "Male" | "Female";
  doctor: string;
  expectedDischarge: string;
  timer?: string;
  status?: "Delayed" | "TPA Pending" | "Initiated";
  categories: string[]; // Multiple active categories for parallel workflows
  initiated?: boolean;
  // Extended fields for drawer
  phone: string;
  admissionNumber: string;
  ward: string;
  bed: string;
  admissionDate: string;
  dischargeDate: string;
  diagnosis: string;
  los: string;
  dischargeStatus: "active" | "discharged";
  elapsedTime?: string;
  /** PD completion time (ISO string). Used to freeze Total Elapsed Time for discharged patients. */
  physicalDischargeCompletionTime?: string | null;
  activityTimeline: ActivityStep[];
  // Workflow fields for list view
  room?: string;
  overallProgress?: number; // 0-100 percentage across all workflow stages
  admissionDateTime?: Date; // For sorting
}

interface PatientCardProps {
  patient: Patient;
  onClick?: () => void;
}

const getTimerColor = (timer: string) => {
  const [hours] = timer.split(":").map(Number);
  if (hours >= 24) return "text-destructive";
  if (hours >= 12) return "text-warning";
  return "text-success";
};

const getTATBorderClass = (status?: string) => {
  switch (status) {
    case 'green': return 'border-l-success';
    case 'yellow': return 'border-l-warning';
    case 'red': return 'border-l-destructive';
    case 'exceeded': return 'border-l-destructive border-l-[6px]';
    default: return 'border-l-primary';
  }
};

const getTATBackgroundClass = (status?: string) => {
  switch (status) {
    case 'green': return 'bg-success/5';
    case 'yellow': return 'bg-warning/5';
    case 'red': return 'bg-destructive/5';
    case 'exceeded': return 'bg-destructive/10';
    default: return 'bg-card';
  }
};

export const PatientCard = ({ patient, onClick }: PatientCardProps) => {
  const tatTimer = usePatientTATTimer(patient.uuid || patient.id);
  const { data: mdCompletionTime } = useMDCompletionTime(patient.uuid || patient.id);
  const isDischarged = patient.dischargeStatus === "discharged";
  const isInitiated = !!mdCompletionTime;
  const isNotInitiatedActive = !isDischarged && !isInitiated;
  
  const getTATColor = (status?: string) => {
    switch (status) {
      case 'green': return 'text-success';
      case 'yellow': return 'text-warning';
      case 'red': return 'text-destructive';
      case 'exceeded': return 'text-destructive font-bold';
      default: return 'text-muted-foreground';
    }
  };

  return (
    <Card
      className={cn(
        "p-1 transition-all duration-200",
        "cursor-pointer select-none",
        "border-l-4",
        // Discharged: keep TAT-based styling
        isDischarged
          ? cn(getTATBorderClass(tatTimer?.status), getTATBackgroundClass(tatTimer?.status), 'dark:text-foreground')
          // Not initiated (Awaiting MD): neutral / grey styling
          : isNotInitiatedActive
          ? "border-l-border bg-card hover:border-border"
          // Otherwise use TAT-based styling
          : cn(
              tatTimer?.status ? "" : "border-l-primary hover:border-[#017BFF]",
              getTATBorderClass(tatTimer?.status),
              getTATBackgroundClass(tatTimer?.status),
            )
      )}
      onClick={onClick}
    >
      <div className="space-y-1">
        {/* Row 1: Name + Timer */}
        <div className="flex items-start justify-between">
          <h4 className="font-medium text-sm text-foreground">{patient.name}</h4>
          <span className={cn("text-xs font-medium", getTATColor(tatTimer?.status))}>
            {tatTimer?.elapsedTime || '--:--'}
          </span>
        </div>

        {/* Row 2: ID, Age, Gender */}
        <div className="flex items-center space-x-1.5 text-xs text-muted-foreground">
          <span>{patient.id}</span>
          <span>•</span>
          <span>{patient.age} yrs</span>
          <span>•</span>
          <span>{patient.gender}</span>
        </div>

        {/* Row 3: Doctor */}
        <div className="flex items-center space-x-1">
          <Stethoscope className="w-3 h-3 text-muted-foreground" />
          <span className="text-xs text-muted-foreground">{isInitiated ? "Initiated by" : "Assigned to"}</span>
          <span className="text-xs font-medium text-foreground">{patient.doctor}</span>
        </div>

        {/* Row 4: Expected Discharge */}
        <div className="flex items-center space-x-1">
          <Clock className="w-3 h-3 text-muted-foreground" />
          <span className="text-xs text-muted-foreground">Expected Discharge:</span>
          <span className="text-xs font-medium text-foreground">{patient.expectedDischarge}</span>
        </div>

        {/* Status Badges */}
        <div className="pt-0.5 flex flex-wrap gap-1">
          {isDischarged ? (
            <Badge className="text-xs px-1 py-0.25 bg-primary text-primary-foreground hover:bg-primary">
              Discharged
            </Badge>
          ) : isInitiated ? (
            <Badge variant="success" className="text-xs px-1 py-0.25">
              Initiated
            </Badge>
          ) : (
            <Badge variant="secondary" className="text-xs px-1 py-0.25">
              Not Initiated
            </Badge>
          )}

          {/* Existing status badge (Delayed / TPA Pending / Initiated, etc.) */}
          {patient.status && (
            <Badge
              variant={
                patient.status === "Delayed"
                  ? "destructive"
                  : patient.status === "TPA Pending"
                  ? "warning"
                  : "success"
              }
              className="text-xs px-1 py-0.25"
            >
              {patient.status}
            </Badge>
          )}
        </div>
      </div>
    </Card>
  );
};
